<template>
  <Teleport to="body">
    <Transition name="exit-modal" :duration="280">
      <div
        v-if="isOpen"
        class="exit-modal__overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-modal-title"
        @click.self="closeModal"
      >
        <section
          ref="modalCard"
          class="exit-modal__card"
          :class="{ 'exit-modal__card--no-header': step !== 'reason' }"
        >
          <header v-if="step === 'reason'" class="exit-modal__header">
            <p>LEÓN · Servicio Automotriz</p>
            <h2 id="exit-modal-title">{{ surveyTitle }}</h2>
            <span v-if="surveySubtitle">{{ surveySubtitle }}</span>
          </header>

          <button type="button" class="exit-modal__close" aria-label="Cerrar modal" @click="closeModal">
            <img
              :src="step === 'reason' ? '/img/modal-exit/close-negative.svg' : '/img/modal-exit/close-positive.svg'"
              alt=""
              width="36"
              height="36"
            >
          </button>

          <div class="exit-modal__body">
            <Transition name="exit-modal-step" mode="out-in">
              <div v-if="step === 'reason'" class="exit-modal__step">
                <button
                  v-for="reason in reasons"
                  :key="reason.id"
                  type="button"
                  class="exit-modal__option"
                  :class="{ 'exit-modal__option--selected': selectedReason?.id === reason.id }"
                  :aria-pressed="selectedReason?.id === reason.id"
                  :disabled="isReasonSubmitting"
                  @click="selectReason(reason)"
                >
                  <span>{{ reason.label }}</span>
                  <span class="exit-modal__radio" aria-hidden="true"></span>
                </button>

                <textarea
                  v-if="selectedReason?.isOther"
                  ref="otherInput"
                  v-model.trim="otherText"
                  class="exit-modal__textarea"
                  maxlength="300"
                  placeholder="Cuéntanos brevemente qué buscabas…"
                  aria-label="Otro motivo"
                ></textarea>

                <p v-if="reasonSubmitError" class="exit-modal__submit-error">No pudimos guardar tu respuesta. Inténtalo nuevamente.</p>

                <button type="button" class="exit-modal__primary" :disabled="!canContinue" @click="continueToContact">
                  <template v-if="isReasonSubmitting">Guardando...</template>
                  <template v-else>Continuar <span aria-hidden="true">➔</span></template>
                </button>
              </div>

              <form
                v-else-if="step === 'contact'"
                class="exit-modal__step"
                novalidate
                @submit.prevent="submitLead(preferredChannel)"
              >
                <button type="button" class="exit-modal__back" @click="step = 'reason'">
                  <img src="/img/modal-exit/arrow_back_ios_new.svg" alt="" width="16" height="16">
                  Volver
                </button>
                <h2 id="exit-modal-title" class="exit-modal__lead-title">Te ayudamos a encontrar lo que buscas</h2>
                <p class="exit-modal__lead-subtitle">Déjanos tus datos y un ejecutivo te llamará a la brevedad.</p>

                <p v-if="submitError" class="exit-modal__submit-error">
                  No pudimos enviar tus datos. Revisa tu conexión y
                  <button type="button" @click="submitLead(preferredChannel)">reintenta</button>.
                </p>

                <div class="exit-modal__field" :class="{ 'exit-modal__field--invalid': errors.name }">
                  <label for="exit-modal-name">Nombre completo</label>
                  <input
                    id="exit-modal-name"
                    ref="nameInput"
                    v-model.trim="name"
                    type="text"
                    autocomplete="name"
                    placeholder="Ej: Camila Rojas Pérez"
                    @input="errors.name && validateName()"
                    @blur="name && validateName()"
                  >
                  <small v-if="errors.name" class="exit-modal__error">{{ errors.name }}</small>
                </div>

                <div class="exit-modal__field" :class="{ 'exit-modal__field--invalid': errors.phone }">
                  <label for="exit-modal-phone">Teléfono de contacto</label>
                  <div class="exit-modal__phone">
                    <div
                      class="exit-modal__country"
                      :class="{ 'exit-modal__country--open': isCountryMenuOpen }"
                      @focusout="handleCountryFocusout"
                    >
                      <button
                        type="button"
                        class="exit-modal__country-button"
                        aria-label="Prefijo telefónico"
                        :aria-expanded="isCountryMenuOpen"
                        @click="isCountryMenuOpen = !isCountryMenuOpen"
                      >
                        <img :src="selectedCountry.flag" :alt="selectedCountry.code" width="20" height="20">
                        <span>{{ selectedCountry.code }} {{ selectedCountry.dialCode }}</span>
                        <span class="exit-modal__country-arrow" aria-hidden="true">▼</span>
                      </button>
                      <div v-if="isCountryMenuOpen" class="exit-modal__country-menu">
                        <button
                          v-for="country in countries"
                          :key="country.code"
                          type="button"
                          class="exit-modal__country-option"
                          :class="{ 'exit-modal__country-option--selected': countryCode === country.dialCode }"
                          @click="selectCountry(country)"
                        >
                          <img :src="country.flag" :alt="country.code" width="20" height="20">
                          <span>{{ country.code }} {{ country.dialCode }}</span>
                        </button>
                      </div>
                    </div>
                    <input
                      id="exit-modal-phone"
                      ref="phoneInput"
                      :value="phone"
                      type="tel"
                      inputmode="numeric"
                      autocomplete="tel-national"
                      :maxlength="selectedCountry.digits + 2"
                      :placeholder="selectedCountry.placeholder"
                      @input="handlePhoneInput"
                      @blur="phone && validatePhone()"
                    >
                  </div>
                  <small v-if="errors.phone" class="exit-modal__error">{{ errors.phone }}</small>
                </div>

                <div class="exit-modal__field" :class="{ 'exit-modal__field--invalid': errors.email }">
                  <label for="exit-modal-email">Correo electrónico</label>
                  <input
                    id="exit-modal-email"
                    ref="emailInput"
                    v-model.trim="email"
                    type="email"
                    autocomplete="email"
                    placeholder="nombre@dominio.cl"
                    @input="errors.email && validateEmail()"
                    @blur="email && validateEmail()"
                  >
                  <small v-if="errors.email" class="exit-modal__error">{{ errors.email }}</small>
                </div>

                <label class="exit-modal__consent" :class="{ 'exit-modal__consent--unchecked': !consent }">
                  <input v-model="consent" type="checkbox" @change="consent && (errors.consent = false)">
                  <span>
                    Acepto la Política de Tratamiento de datos personales según Ley Nº 21.459.
                    <NuxtLink to="/terminos-y-condiciones" target="_blank">Leer Políticas</NuxtLink>
                  </span>
                </label>
                <p v-if="errors.consent" class="exit-modal__consent-error">Debes aceptar el tratamiento de datos personales para continuar.</p>

                <p class="exit-modal__channel-label">Quiero que me contacten:</p>
                <div class="exit-modal__actions">
                  <button type="submit" class="exit-modal__primary" :disabled="isSubmitting" @click="preferredChannel = 'Llamada'">
                    <template v-if="isSubmitting && preferredChannel === 'Llamada'">Enviando...</template>
                    <template v-else>
                      <img src="/img/modal-exit/phone_in_talk-positive.svg" alt="" aria-hidden="true">
                      Por llamada
                    </template>
                  </button>
                  <button type="submit" class="exit-modal__primary exit-modal__primary--whatsapp" :disabled="isSubmitting" @click="preferredChannel = 'WhatsApp'">
                    <template v-if="isSubmitting && preferredChannel === 'WhatsApp'">Enviando...</template>
                    <template v-else>
                      <img src="/img/modal-exit/whatsapp-v1.svg" alt="" aria-hidden="true">
                      Por WhatsApp
                    </template>
                  </button>
                </div>
              </form>

              <div v-else class="exit-modal__step exit-modal__success">
                <span class="exit-modal__check" aria-hidden="true"></span>
                <h2 id="exit-modal-title">{{ thanksTitle }}</h2>
                <p>{{ thanksMessage }}</p>
                <button type="button" class="exit-modal__primary" @click="closeModal">Seguir viendo neumáticos</button>
              </div>
            </Transition>
          </div>
        </section>
      </div>
    </Transition>

    <button
      v-if="isFloatingVisible"
      type="button"
      class="exit-modal__floating"
      title="¿Tienes dudas? Te ayudamos"
      aria-label="¿Tienes dudas? Te ayudamos"
      @click="openModal('trigger-icon')"
    >
      <img src="/img/modal-exit/preguntas-exit-intent.svg" alt="" width="34" height="34">
    </button>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const isOpen = useState('exit-intent-modal-open', () => false);
const route = useRoute();
const { getDeviceUUID, getSearchUUID } = useGeneradorUUIDS();
const { updateDashboardPersona } = useDashboardNeu();
const { cargarPreguntaActiva, registrarPopupMostrado, completarFormulario } = useFormularioExitIntent();
const { trace } = useTraceLog('components->Modals->ModalExitIntent.vue');
const modalCard = ref(null);
const otherInput = ref(null);
const nameInput = ref(null);
const phoneInput = ref(null);
const emailInput = ref(null);
const step = ref('reason');
const selectedReason = ref(null);
const otherText = ref('');
const name = ref('');
const phone = ref('');
const email = ref('');
const countryCode = ref('+56');
const isCountryMenuOpen = ref(false);
const consent = ref(true);
const preferredChannel = ref('Llamada');
const errors = ref({ name: '', phone: '', email: '', consent: false });
const isSubmitting = ref(false);
const submitError = ref(false);
const isReasonSubmitting = ref(false);
const reasonSubmitError = ref(false);
const storedReasonId = ref(null);
const thanksTitle = ref('¡Listo! Te contactaremos a la brevedad');
const thanksMessage = ref('Un ejecutivo especializado revisará tu solicitud para asesorarte sin costo.');
const popupTrigger = ref('mouse-exit');
const isMounted = ref(false);
const wasAutoShown = ref(false);
const isLeadSent = ref(false);
let previousOverflow = '';
let lastScrollY = 0;
let lastScrollTime = 0;
let idleTimer = null;
let isBackTriggerArmed = false;
let ignoreNextPopstate = false;
let backClosesModal = false;
let touchModeQuery = null;

const PLP_PATH_PREFIX = '/neumaticos/plp';
// Misma condición que el media query mobile del <style>: puntero táctil o viewport menor a 768px.
const TOUCH_MODE_QUERY = '(pointer: coarse), (max-width: 767px)';
const SHOWN_SESSION_KEY = 'leonExitIntentShown';
const LEAD_SESSION_KEY = 'leonExitIntentLeadSent';
const BACK_HISTORY_STATE_KEY = 'leonExitIntentBackGuard';
const EXIT_TOP_THRESHOLD_PX = 25;
const SCROLL_UP_MIN_SPEED_PX_S = 900;
const SCROLL_UP_MAX_Y_PX = 400;
const INACTIVITY_MS = 25000;
const IDLE_RESET_EVENTS = ['touchstart', 'scroll', 'keydown', 'click'];

const countries = [
  { code: 'CL', name: 'Chile', dialCode: '+56', digits: 9, groups: [1, 4, 4], placeholder: '9 1234 5678', flag: '/img/modal-exit/flag-cl.svg' },
  { code: 'AR', name: 'Argentina', dialCode: '+54', digits: 10, groups: [2, 4, 4], placeholder: '11 1234 5678', flag: '/img/modal-exit/flag-ar.svg' },
];

const selectedCountry = computed(() => (
  countries.find(country => country.dialCode === countryCode.value) || countries[0]
));

// Pregunta, encabezado y motivos cargados desde form_pregunta/form_opcion: reflejan lo que se edita en Configuración del dashboard.
const surveyQuestionId = ref(null);
const surveyTitle = ref('');
const surveySubtitle = ref('');
const reasons = ref([]);
// Sin motivos cargados (Control Master apagado o configuración no disponible) el popup no se abre ni ofrece el botón flotante.
const isSurveyReady = computed(() => reasons.value.length > 0);

function esOtroMotivo(texto) {
  return /^otr[oa]\b/.test(texto.trim().toLocaleLowerCase('es-CL'));
}

// Handoff §3 (regla CRO de baja intención): a quien "solo estaba mirando" no se le piden datos de contacto.
function esSoloMirando(texto) {
  return /^solo estaba mirando\b/.test(texto.trim().toLocaleLowerCase('es-CL'));
}

async function loadSurveyQuestion() {
  const pregunta = await cargarPreguntaActiva();
  if (!pregunta) return; // Control Master apagado en el dashboard.

  const options = pregunta.opciones
    .slice()
    .sort((first, second) => Number(esOtroMotivo(first.valor_opcion)) - Number(esOtroMotivo(second.valor_opcion)));

  surveyQuestionId.value = pregunta.idPregunta;
  surveyTitle.value = pregunta.titulo;
  surveySubtitle.value = pregunta.subtitulo || '';
  reasons.value = options.map(option => ({
    id: option.id_opcion,
    label: option.valor_opcion,
    isOther: esOtroMotivo(option.valor_opcion),
    isJustLooking: esSoloMirando(option.valor_opcion),
  }));
}

let reasonsPromise = null;
function ensureReasonsLoaded() {
  if (!reasonsPromise) {
    reasonsPromise = loadSurveyQuestion().catch(error => {
      reasonsPromise = null;
      trace('loadSurveyQuestion() error:', error);
      throw error;
    });
  }
  return reasonsPromise;
}

const canContinue = computed(() => {
  if (!selectedReason.value || isReasonSubmitting.value) return false;
  return !selectedReason.value.isOther || otherText.value.length > 0;
});

// Tras cerrar sin dejar datos, el botón flotante permite reabrir el modal (handoff §2.C).
const isFloatingVisible = computed(() => (
  isMounted.value
  && isSurveyReady.value
  && route.path.startsWith(PLP_PATH_PREFIX)
  && wasAutoShown.value
  && !isLeadSent.value
  && !isOpen.value
));

function trackEvent(event, data = {}) {
  if (!import.meta.client) return;

  const payload = {
    event,
    ...data,
    page: window.location.href,
    device: window.innerWidth < 768 ? 'mobile' : 'desktop',
    timestamp: new Date().toISOString(),
  };

  document.dispatchEvent(new CustomEvent('leon:exit-intent', { detail: payload }));

  try {
    const storedEvents = JSON.parse(localStorage.getItem('leonExitEvents') || '[]');
    localStorage.setItem('leonExitEvents', JSON.stringify([...storedEvents.slice(-99), payload]));
  } catch (_) {}
}

// sessionStorage puede lanzar (almacenamiento bloqueado): sin persistencia el modal sigue funcionando en memoria.
function readSessionFlag(key) {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch (_) {
    return false;
  }
}

function writeSessionFlag(key) {
  try {
    sessionStorage.setItem(key, '1');
  } catch (_) {}
}

function resetForm() {
  step.value = 'reason';
  selectedReason.value = null;
  otherText.value = '';
  name.value = '';
  phone.value = '';
  email.value = '';
  countryCode.value = '+56';
  isCountryMenuOpen.value = false;
  consent.value = true;
  errors.value = { name: '', phone: '', email: '', consent: false };
  isSubmitting.value = false;
  submitError.value = false;
  isReasonSubmitting.value = false;
  reasonSubmitError.value = false;
  storedReasonId.value = null;
}

function formatPhone(value) {
  const { digits, groups } = selectedCountry.value;
  const numbers = value.replace(/\D/g, '').slice(0, digits);
  const parts = [];
  let start = 0;

  for (const size of groups) {
    if (start >= numbers.length) break;
    parts.push(numbers.slice(start, start + size));
    start += size;
  }

  return parts.join(' ');
}

function handlePhoneInput(event) {
  phone.value = formatPhone(event.target.value);
  event.target.value = phone.value;
  if (errors.value.phone) validatePhone();
}

function validateName() {
  const length = name.value.length;
  errors.value.name = length === 0
    ? 'Por favor ingresa tu nombre completo'
    : length < 2 ? 'El nombre debe tener al menos 2 caracteres' : '';
  return !errors.value.name;
}

function validatePhone() {
  const { digits, name: countryName } = selectedCountry.value;
  const count = phone.value.replace(/\D/g, '').length;
  errors.value.phone = count === 0
    ? 'Por favor ingresa tu número de teléfono'
    : count < digits ? `Faltan números: el teléfono para ${countryName} debe tener ${digits} dígitos (ingresaste ${count})` : '';
  return !errors.value.phone;
}

function validateEmail() {
  const valor = email.value.trim();
  errors.value.email = valor === ''
    ? 'Por favor ingresa tu correo electrónico'
    : !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(valor) ? 'Ingresa un correo electrónico válido (Ej: nombre@dominio.cl)' : '';
  return !errors.value.email;
}

function selectCountry(country) {
  countryCode.value = country.dialCode;
  isCountryMenuOpen.value = false;
  if (!phone.value) return;

  phone.value = formatPhone(phone.value);
  validatePhone();
}

function handleCountryFocusout(event) {
  if (!event.currentTarget.contains(event.relatedTarget)) isCountryMenuOpen.value = false;
}

async function selectReason(reason) {
  selectedReason.value = reason;
  storedReasonId.value = null;
  reasonSubmitError.value = false;

  if (reason.isOther) {
    await nextTick();
    otherInput.value?.focus();
    return;
  }

  otherText.value = '';
  await submitReasonStore(reason, null);
}

async function submitReasonStore(reason, customReason) {
  isReasonSubmitting.value = true;
  reasonSubmitError.value = false;
  let payload = null;

  try {
    await ensureReasonsLoaded();

    payload = {
      respuesta: {
        id_pregunta: surveyQuestionId.value,
        opciones: [{ id_opcion: reason.id, otro_motivo_respuesta: reason.isOther ? (customReason || null) : null }],
      },
      lead: null,
      metadata: {
        metadata: [
          { nombre_metadata: 'dispositivo', valor_metadata: window.innerWidth < 768 ? 'mobile' : 'desktop' },
          { nombre_metadata: 'dashboard_uuid', valor_metadata: await getSearchUUID(false) },
        ],
      },
      estado: { valor_estado: 'formulario_completado' },
    };

    await completarFormulario(payload);
    storedReasonId.value = reason.id;
    trackEvent('exit_reason_stored', payload);
    return true;
  } catch (error) {
    reasonSubmitError.value = true;
    trace('submitReasonStore() error:', error);
    trackEvent('exit_reason_store_error', { message: String(error), ...payload });
    return false;
  } finally {
    isReasonSubmitting.value = false;
  }
}

async function continueToContact() {
  if (!canContinue.value) return;

  if (storedReasonId.value !== selectedReason.value.id) {
    const stored = await submitReasonStore(
      selectedReason.value,
      selectedReason.value.isOther ? otherText.value : null,
    );
    if (!stored) return;
  }

  trackEvent('exit_reason', {
    reasonId: selectedReason.value.id,
    reasonLabel: selectedReason.value.label,
    otherText: otherText.value,
  });

  if (selectedReason.value.isJustLooking) {
    thanksTitle.value = '¡Gracias por tu visita!';
    thanksMessage.value = 'Si necesitas ayuda o asesoría en otro momento, estaremos felices de atenderte.';
    step.value = 'thanks';
    return;
  }

  step.value = 'contact';
}

// El contacto del lead también se guarda en la persona del dashboard (uuid de dispositivo). El lead ya quedó
// registrado en el formulario: esta llamada no se espera ni propaga errores (mismo patrón que Ruleta y
// PersonalInformation), para que un dashboard lento o caído no retrase ni falle el "gracias".
async function updateDashboardPersonaLead(normalizedPhone) {
  try {
    const nameParts = name.value.trim().split(/\s+/);
    const firstName = nameParts.shift() || '';
    const paternalSurname = nameParts.shift() || '';
    const maternalSurname = nameParts.join(' ');

    await updateDashboardPersona(await getDeviceUUID(), {
      nombre: firstName,
      apellido_paterno: paternalSurname,
      apellido_materno: maternalSurname,
      celular: normalizedPhone,
      email: email.value,
    });
  } catch (error) {
    trace('updateDashboardPersonaLead() error:', error);
  }
}

async function submitLead(channel) {
  const normalizedPhone = phone.value.replace(/\D/g, '');
  let customerLocation = 'No informada';

  try {
    customerLocation = JSON.parse(localStorage.getItem('ubication_confirmed') || '{}').address || customerLocation;
  } catch (_) {}

  const isNameValid = validateName();
  const isPhoneValid = validatePhone();
  const isEmailValid = validateEmail();
  errors.value.consent = !consent.value;

  if (!isNameValid || !isPhoneValid || !isEmailValid || errors.value.consent) {
    if (!isNameValid) nameInput.value?.focus();
    else if (!isPhoneValid) phoneInput.value?.focus();
    else if (!isEmailValid) emailInput.value?.focus();
    return;
  }

  isSubmitting.value = true;
  submitError.value = false;

  try {
    await ensureReasonsLoaded();

    const payload = {
      respuesta: {
        id_pregunta: surveyQuestionId.value,
        opciones: [{ id_opcion: selectedReason.value.id, otro_motivo_respuesta: selectedReason.value.isOther ? otherText.value : null }],
      },
      lead: {
        nombre_lead: name.value,
        telefono_lead: `${countryCode.value}${normalizedPhone}`,
        email_lead: email.value,
      },
      metadata: {
        metadata: [
          { nombre_metadata: 'dispositivo', valor_metadata: window.innerWidth < 768 ? 'mobile' : 'desktop' },
          { nombre_metadata: 'canal_contacto', valor_metadata: channel },
          // Sin dirección confirmada no se envía: "No informada" taparía el local de la búsqueda en el dashboard.
          ...(customerLocation !== 'No informada' ? [{ nombre_metadata: 'ubicacion_cliente', valor_metadata: customerLocation }] : []),
          { nombre_metadata: 'dashboard_uuid', valor_metadata: await getSearchUUID(false) },
        ],
      },
      estado: { valor_estado: 'formulario_completado' },
    };

    await completarFormulario(payload);
    updateDashboardPersonaLead(normalizedPhone);

    trackEvent('lead_request', {
      ...payload,
      reasonId: selectedReason.value.id,
      reasonLabel: selectedReason.value.label,
      otherText: otherText.value,
      preferredChannel: channel,
    });

    isLeadSent.value = true;
    writeSessionFlag(LEAD_SESSION_KEY);
    thanksTitle.value = '¡Listo! Te contactaremos a la brevedad';
    thanksMessage.value = channel === 'WhatsApp'
      ? 'Un ejecutivo especializado se pondrá en contacto contigo por WhatsApp a la brevedad.'
      : 'Un ejecutivo especializado te llamará a la brevedad para asesorarte sin costo.';
    step.value = 'thanks';
  } catch (error) {
    submitError.value = true;
    trace('submitLead() error:', error);
    trackEvent('lead_request_error', { message: String(error) });
  } finally {
    isSubmitting.value = false;
  }
}

function isTouchMode() {
  return (touchModeQuery || window.matchMedia(TOUCH_MODE_QUERY)).matches;
}

function isPlpRoute() {
  return route.path.startsWith(PLP_PATH_PREFIX);
}

function canAutoOpen() {
  return isSurveyReady.value && !wasAutoShown.value && !isOpen.value && isPlpRoute();
}

// Apertura automática: una vez por sesión. El botón flotante ('trigger-icon') reabre sin límite.
function openModal(trigger) {
  if (isOpen.value) return;

  if (trigger !== 'trigger-icon') {
    if (wasAutoShown.value) return;
    wasAutoShown.value = true;
    writeSessionFlag(SHOWN_SESSION_KEY);
  }

  clearTimeout(idleTimer);
  // En modo táctil el botón atrás cierra el modal (centinela en el historial) en vez de salir de la PLP.
  if (isTouchMode()) {
    pushBackSentinel();
    backClosesModal = true;
  }
  popupTrigger.value = trigger;
  isOpen.value = true;
}

// Desktop: el cursor abandona la ventana por el borde superior.
function handleMouseOut(event) {
  if (event.relatedTarget || event.clientY > EXIT_TOP_THRESHOLD_PX || isTouchMode() || !canAutoOpen()) return;
  openModal('mouse-exit');
}

// Mobile: scroll rápido hacia arriba cerca del inicio de la página.
function handleScroll() {
  const currentY = window.scrollY;
  const now = performance.now();
  const upwardSpeed = ((lastScrollY - currentY) / Math.max(now - lastScrollTime, 1)) * 1000;
  lastScrollY = currentY;
  lastScrollTime = now;

  if (upwardSpeed > SCROLL_UP_MIN_SPEED_PX_S && currentY < SCROLL_UP_MAX_Y_PX && isTouchMode() && canAutoOpen()) {
    openModal('scroll-up');
  }
}

// Mobile: inactividad sin interacción.
function resetIdleTimer() {
  clearTimeout(idleTimer);
  idleTimer = isTouchMode() && canAutoOpen() ? setTimeout(() => openModal('inactivity'), INACTIVITY_MS) : null;
}

function handleViewportChange(event) {
  if (event.matches) {
    armBackTrigger();
  } else {
    releaseBackTrigger();
  }
  resetIdleTimer();
}

// Mobile: botón atrás mediante una entrada centinela en el historial.
function pushBackSentinel() {
  if (window.history.state?.[BACK_HISTORY_STATE_KEY] !== route.path) {
    window.history.pushState(
      { ...window.history.state, [BACK_HISTORY_STATE_KEY]: route.path },
      '',
      window.location.href,
    );
  }
  isBackTriggerArmed = true;
}

function armBackTrigger() {
  if (!isTouchMode() || isBackTriggerArmed || !canAutoOpen()) return;
  pushBackSentinel();
}

function releaseBackTrigger() {
  if (!isBackTriggerArmed) return;

  ignoreNextPopstate = true;
  isBackTriggerArmed = false;
  window.history.back();
}

function handlePopstate() {
  if (ignoreNextPopstate) {
    ignoreNextPopstate = false;
    return;
  }

  if (isOpen.value && backClosesModal) {
    isBackTriggerArmed = false;
    backClosesModal = false;
    trackEvent('popup_closed', { lastStep: step.value });
    isOpen.value = false;
    return;
  }

  if (!isBackTriggerArmed || !isTouchMode() || !canAutoOpen()) return;
  openModal('mobile-back');
}

function closeModal() {
  trackEvent('popup_closed', { lastStep: step.value });
  isOpen.value = false;
  if (backClosesModal) {
    backClosesModal = false;
    releaseBackTrigger();
  }
}

function handleKeydown(event) {
  if (event.key === 'Escape') closeModal();
}

watch(isOpen, async (open) => {
  if (!import.meta.client) { return; }

  if (open) {
    resetForm();
    previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeydown);
    trackEvent('popup_shown', { trigger: popupTrigger.value });
    // En serie: la carga de motivos abre la sesión si no existe y el registro de "mostrado" la reutiliza.
    ensureReasonsLoaded()
      .then(() => registrarPopupMostrado())
      .catch(error => trace('registrarPopupMostrado() error:', error));
    await nextTick();
    modalCard.value?.querySelector('.exit-modal__option')?.focus({ preventScroll: true });
  } else {
    document.documentElement.style.overflow = previousOverflow;
    document.removeEventListener('keydown', handleKeydown);
  }
});

watch(() => route.path, async () => {
  if (!import.meta.client) return;

  // El modal vive en el layout: no debe quedar abierto sobre otra página (sin tocar el historial en plena navegación).
  if (isOpen.value) {
    backClosesModal = false;
    closeModal();
  }
  // Al salir de la PLP el centinela queda atrás en el historial; se vuelve a armar al regresar.
  if (!isPlpRoute()) isBackTriggerArmed = false;
  else ensureReasonsLoaded().catch(() => {});
  await nextTick();
  armBackTrigger();
  resetIdleTimer();
});

// La configuración llega después del montaje: recién entonces se arman el botón atrás y la inactividad (móvil).
watch(isSurveyReady, () => {
  armBackTrigger();
  resetIdleTimer();
});

onMounted(() => {
  isMounted.value = true;
  // El modal vive en el layout pero solo se abre en la PLP: fuera de ella no se consulta ni se abre sesión.
  if (isPlpRoute()) ensureReasonsLoaded().catch(() => {});
  wasAutoShown.value = readSessionFlag(SHOWN_SESSION_KEY);
  isLeadSent.value = readSessionFlag(LEAD_SESSION_KEY);
  lastScrollY = window.scrollY;
  lastScrollTime = performance.now();
  touchModeQuery = window.matchMedia(TOUCH_MODE_QUERY);
  document.addEventListener('mouseout', handleMouseOut);
  window.addEventListener('scroll', handleScroll, { passive: true });
  IDLE_RESET_EVENTS.forEach(eventName => window.addEventListener(eventName, resetIdleTimer, { passive: true }));
  window.addEventListener('popstate', handlePopstate);
  touchModeQuery.addEventListener('change', handleViewportChange);
  armBackTrigger();
  resetIdleTimer();
});

onBeforeUnmount(() => {
  if (!import.meta.client) { return; }
  // El layout monta el modal solo en la PLP y isOpen es estado global: sin este reset, al volver a la PLP
  // el modal quedaría abierto sin listeners ni formulario inicializado.
  isOpen.value = false;
  clearTimeout(idleTimer);
  document.documentElement.style.overflow = previousOverflow;
  document.removeEventListener('keydown', handleKeydown);
  document.removeEventListener('mouseout', handleMouseOut);
  window.removeEventListener('scroll', handleScroll);
  IDLE_RESET_EVENTS.forEach(eventName => window.removeEventListener(eventName, resetIdleTimer));
  window.removeEventListener('popstate', handlePopstate);
  touchModeQuery?.removeEventListener('change', handleViewportChange);
});
</script>

<style scoped>
/* Valores de referencia: release-fase-1/leon-exit-intent-demo.html (UX/UI). */
.exit-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 15, 20, 0.55);
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
}

.exit-modal__card {
  position: relative;
  display: flex;
  width: min(520px, calc(100vw - 32px));
  max-height: min(86vh, 720px);
  flex-direction: column;
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  color: #1c1c22;
  font-family: "Nunito", sans-serif;
  line-height: normal;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
}

.exit-modal__header {
  position: relative;
  flex: none;
  overflow: hidden;
  padding: 18px 60px 16px 22px;
  background: #c8102e;
  color: #fff;
}

.exit-modal__header::after {
  position: absolute;
  top: -90px;
  right: -70px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.07);
  content: "";
  pointer-events: none;
}

.exit-modal__header p {
  margin: 0;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.16em;
  opacity: 0.92;
  text-transform: uppercase;
}

.exit-modal__header h2 {
  margin: 5px 0 0;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.2;
}

.exit-modal__header span {
  position: relative;
  z-index: 1;
  display: block;
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.92);
  font-size: 14.5px;
  line-height: 1.45;
}

.exit-modal__close {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  display: flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: transform 0.15s ease;
}

.exit-modal__close:hover {
  background: rgba(0, 0, 0, 0.12);
  transform: scale(1.08);
}

.exit-modal__close img {
  display: block;
  width: 100%;
  height: 100%;
}

.exit-modal__body {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 24px;
}

.exit-modal__card--no-header .exit-modal__body {
  padding-top: 40px;
}

.exit-modal__option {
  display: flex;
  width: 100%;
  min-height: 48px;
  align-items: center;
  gap: 10px;
  margin-bottom: 9px;
  padding: 12px 14px;
  border: 1.5px solid #e4e4ea;
  border-radius: 12px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 14.5px;
  text-align: left;
  user-select: none;
  transition: border-color 0.15s, background 0.15s;
}

.exit-modal__option:hover {
  border-color: #c8c8d2;
}

.exit-modal__option--selected,
.exit-modal__option--selected:hover {
  border-color: #238b00;
  background: #f2f9f0;
}

.exit-modal__radio {
  position: relative;
  width: 22px;
  height: 22px;
  flex: none;
  margin-left: auto;
  border: 2px solid #d4d4dc;
  border-radius: 50%;
}

.exit-modal__option--selected .exit-modal__radio {
  border-color: #238b00;
  background: #238b00;
}

.exit-modal__option--selected .exit-modal__radio::after {
  position: absolute;
  top: 45%;
  left: 50%;
  width: 4px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  content: "";
  transform: translate(-50%, -50%) rotate(45deg);
}

.exit-modal__textarea {
  display: block;
  width: 100%;
  min-height: 74px;
  margin: 2px 0 10px;
  padding: 11px 13px;
  border: 1.5px solid #e4e4ea;
  border-radius: 12px;
  background: #fff;
  color: inherit;
  font: inherit;
  font-size: 16px;
  resize: vertical;
}

.exit-modal__primary {
  display: inline-flex;
  width: 100%;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  overflow: hidden;
  padding: 12px 10px;
  border: 0;
  border-radius: 12px;
  background: #16a34a;
  color: #fff;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 700;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background 0.15s, opacity 0.15s;
}

.exit-modal__primary:hover:not(:disabled) {
  background: #12833c;
}

.exit-modal__primary:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.exit-modal__primary--whatsapp {
  background: #25d366;
}

.exit-modal__primary--whatsapp:hover:not(:disabled) {
  background: #1da851;
}

.exit-modal__primary img {
  display: block;
  width: 18px;
  height: 18px;
  flex: none;
}

.exit-modal__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: -4px 0 14px;
  padding: 6px 10px 6px 0;
  border: 0;
  background: none;
  color: #c8102e;
  cursor: pointer;
  font-size: 16.5px;
  font-weight: 800;
  transition: transform 0.15s ease;
}

.exit-modal__back:hover {
  transform: translateX(-3px);
}

.exit-modal__back img {
  display: block;
  width: 16px;
  height: 16px;
}

.exit-modal__lead-title {
  margin: 0 0 4px;
  font-size: 19px;
  font-weight: 800;
}

.exit-modal__lead-subtitle {
  margin: 0 0 14px;
  color: #55555f;
  font-size: 13.5px;
}

.exit-modal__submit-error {
  margin: 0 0 12px;
  padding: 11px 14px;
  border: 1.5px solid #fca5a5;
  border-radius: 12px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 13.5px;
}

.exit-modal__submit-error button {
  margin-left: 4px;
  padding: 0;
  border: 0;
  background: none;
  color: #b91c1c;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 700;
  text-decoration: underline;
}

.exit-modal__field {
  margin-bottom: 16px;
}

.exit-modal__field > label {
  display: block;
  margin-bottom: 8px;
  font-size: 13.5px;
  font-weight: 700;
}

.exit-modal__field input {
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #e4e4ea;
  border-radius: 12px;
  background: #fff;
  color: inherit;
  font: inherit;
  font-size: 15px;
}

/* box-shadow: none anula el anillo de foco que agrega la base de flowbite. */
.exit-modal__textarea:focus,
.exit-modal__field input:focus {
  border-color: #1d4ed7;
  outline: 2px solid #1d4ed7;
  outline-offset: 1px;
  box-shadow: none;
}

.exit-modal__field--invalid input,
.exit-modal__field--invalid input:focus {
  border: 2px solid #dc2626;
  background: #fff8f8;
  outline: none;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12);
  animation: exit-modal-shake 0.2s ease-in-out;
}

.exit-modal__field--invalid input:focus {
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.2);
}

.exit-modal__error {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  color: #dc2626;
  font-size: 12.5px;
  font-weight: 700;
}

.exit-modal__phone {
  display: flex;
  gap: 8px;
}

.exit-modal__phone input {
  min-width: 0;
  flex: 1;
}

.exit-modal__country {
  position: relative;
  flex: none;
}

.exit-modal__country-button {
  display: flex;
  height: 48px;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1.5px solid #e4e4ea;
  border-radius: 12px;
  background: #f7f7f9;
  color: #3a3f4c;
  cursor: pointer;
  font-size: 15px;
  font-weight: 800;
  user-select: none;
  transition: border-color 0.15s;
}

.exit-modal__country-button:hover {
  border-color: #c8c8d2;
}

.exit-modal__country-button img,
.exit-modal__country-option img {
  width: 20px;
  height: 20px;
  flex: none;
  border-radius: 50%;
  object-fit: cover;
}

.exit-modal__country-arrow {
  margin-left: 2px;
  color: #6b7280;
  font-size: 10px;
  transition: transform 0.15s;
}

.exit-modal__country--open .exit-modal__country-arrow {
  transform: rotate(180deg);
}

.exit-modal__country-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 10;
  width: 140px;
  overflow: hidden;
  border: 1.5px solid #e4e4ea;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.exit-modal__country-option {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 0;
  background: #fff;
  color: #1c1c22;
  cursor: pointer;
  font-size: 14.5px;
  font-weight: 700;
  transition: background 0.15s;
}

.exit-modal__country-option:hover,
.exit-modal__country-option--selected {
  background: #fdf2f4;
  color: #c8102e;
}

.exit-modal__consent {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
  padding: 12px 14px;
  border: 1.5px dashed #10b981;
  border-radius: 12px;
  background: #ecfdf5;
  color: #065f46;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.4;
  text-align: left;
  user-select: none;
  transition: all 0.2s ease-in-out;
}

.exit-modal__consent--unchecked {
  border-color: #b91c1c;
  background: #fef2f2;
  color: #7f1d1d;
}

.exit-modal__consent input {
  display: inline-grid;
  width: 22px;
  height: 22px;
  flex: none;
  place-content: center;
  margin: 0;
  border: 2px solid #a7f3d0;
  border-radius: 6px;
  appearance: none;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.exit-modal__consent input:focus {
  box-shadow: none;
}

.exit-modal__consent input:focus-visible {
  outline: 2px solid #1d4ed7;
  outline-offset: 2px;
}

.exit-modal__consent--unchecked input {
  border-color: #fca5a5;
}

.exit-modal__consent input:checked {
  border-color: #15803d;
  background: #15803d;
}

.exit-modal__consent input:checked::before {
  width: 11px;
  height: 6px;
  border-bottom: 2.5px solid #fff;
  border-left: 2.5px solid #fff;
  content: "";
  transform: rotate(-45deg) translate(1px, -1px);
}

.exit-modal__consent a {
  color: inherit;
  font-weight: 700;
  text-decoration: underline;
}

.exit-modal__consent a:hover {
  opacity: 0.85;
}

.exit-modal__consent-error {
  margin: -8px 0 12px;
  color: #dc2626;
  font-size: 12.5px;
}

.exit-modal__channel-label {
  margin: 22px 0 12px;
  font-size: 15px;
  font-weight: 800;
  text-align: center;
}

.exit-modal__actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.exit-modal__actions .exit-modal__primary {
  min-width: 0;
  flex: 1;
}

.exit-modal__success {
  padding: 6px 0 2px;
  text-align: center;
}

.exit-modal__check {
  position: relative;
  display: block;
  width: 74px;
  height: 74px;
  margin: 4px auto 16px;
  border-radius: 50%;
  background: #e9f7ee;
}

.exit-modal__check::after {
  position: absolute;
  top: 17px;
  left: 26px;
  width: 16px;
  height: 30px;
  border: solid #16a34a;
  border-width: 0 5px 5px 0;
  border-radius: 2px;
  content: "";
  transform: rotate(45deg);
}

.exit-modal__success h2 {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 800;
}

.exit-modal__success p {
  margin: 0 0 16px;
  color: #55555f;
  font-size: 14.5px;
  line-height: 1.55;
}

.exit-modal__success .exit-modal__primary {
  margin-top: 16px;
}

/* Desviación del demo (right 24 / bottom 70 / z 999999): se alinea sobre la burbuja del chat Vambee para no taparla.
   La burbuja sube con el popup combinado (.chat-bottom-popup). Su estado .chat-bottom-low aplica solo a rutas de
   instalación/agendamiento/checkout, donde este botón no se muestra (solo PLP). */
.exit-modal__floating {
  position: fixed;
  right: 20px;
  bottom: 135px;
  z-index: 200;
  display: flex;
  width: 58px;
  height: 58px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 2px solid #c8102e;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 8px 25px rgba(200, 16, 46, 0.35);
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  animation: exit-modal-breathing 3s ease-in-out infinite;
}

.exit-modal__floating:hover {
  box-shadow: 0 12px 32px rgba(200, 16, 46, 0.5);
  transform: scale(1.1);
}

.exit-modal__floating img {
  display: block;
  width: 34px;
  height: 34px;
  object-fit: contain;
  pointer-events: none;
}

.exit-modal-enter-active,
.exit-modal-leave-active {
  transition: opacity 0.25s ease;
}

.exit-modal-enter-active .exit-modal__card,
.exit-modal-leave-active .exit-modal__card {
  transition: transform 0.28s cubic-bezier(0.2, 0.9, 0.3, 1.15);
}

.exit-modal-enter-from,
.exit-modal-leave-to {
  opacity: 0;
}

.exit-modal-enter-from .exit-modal__card,
.exit-modal-leave-to .exit-modal__card {
  transform: translateY(14px) scale(0.98);
}

.exit-modal-step-enter-active {
  animation: exit-modal-step-in 0.25s ease;
}

@keyframes exit-modal-step-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes exit-modal-shake {
  0%,
  100% {
    transform: translateX(0);
  }

  25% {
    transform: translateX(-3px);
  }

  75% {
    transform: translateX(3px);
  }
}

@keyframes exit-modal-breathing {
  0%,
  100% {
    box-shadow: 0 6px 20px rgba(200, 16, 46, 0.3), 0 0 0 0 rgba(200, 16, 46, 0.4);
    transform: scale(1);
  }

  50% {
    box-shadow: 0 14px 30px rgba(200, 16, 46, 0.5), 0 0 0 12px rgba(200, 16, 46, 0);
    transform: scale(1.08);
  }
}

:global(body:has(.chat-bottom-popup)) .exit-modal__floating {
  bottom: 235px !important;
}

/* Mismo breakpoint que el chat Vambee (components/Vambee/Chat.vue). */
@media (max-width: 768px) {
  .exit-modal__floating {
    bottom: 170px;
  }
}

/* Misma condición que TOUCH_MODE_QUERY del script. */
@media (max-width: 767px), (pointer: coarse) {
  .exit-modal__overlay {
    padding: 12px;
  }

  .exit-modal__card {
    width: min(440px, calc(100vw - 24px));
    max-height: min(88vh, 600px);
  }

  .exit-modal__header {
    padding: 14px 48px 12px 18px;
  }

  .exit-modal__header h2 {
    font-size: 20px;
  }

  .exit-modal__header span {
    margin-top: 4px;
    font-size: 13px;
  }

  .exit-modal__body,
  .exit-modal__card--no-header .exit-modal__body {
    padding: 14px 16px 16px;
  }

  .exit-modal__option {
    min-height: 44px;
    margin-bottom: 7px;
    padding: 10px 12px;
    font-size: 13.5px;
  }

  .exit-modal__textarea {
    min-height: 60px;
  }

  /* Desviación del demo (15px / 14.5px): iOS Safari hace zoom al enfocar campos de menos de 16px. */
  .exit-modal__field input {
    font-size: 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .exit-modal-enter-active,
  .exit-modal-leave-active,
  .exit-modal-enter-active .exit-modal__card,
  .exit-modal-leave-active .exit-modal__card {
    transition: none;
  }

  .exit-modal-step-enter-active,
  .exit-modal__floating {
    animation: none;
  }
}
</style>
